import { cn } from '@/utils/helpers';
import { forwardRef, useState, useEffect } from 'react';

interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  alt?: string;
  fallback?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  shape?: 'circle' | 'square';
}

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

const shapeClasses = {
  circle: 'rounded-full',
  square: 'rounded-lg',
};

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, src, alt, fallback, size = 'md', shape = 'circle', ...props }, ref) => {
    const [showFallback, setShowFallback] = useState(false);

    // Reset fallback state when src changes
    useEffect(() => {
      setShowFallback(false);
    }, [src]);

    if (showFallback || !src) {
      return (
        <div
          ref={ref}
          className={cn(
            'inline-flex items-center justify-center font-medium bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200',
            sizeClasses[size],
            shapeClasses[shape],
            className
          )}
          {...props}
        >
          {fallback || '?'}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          'relative inline-block overflow-hidden',
          sizeClasses[size],
          shapeClasses[shape],
          className
        )}
        {...props}
      >
        <img
          src={src}
          alt={alt || fallback || 'Avatar'}
          className="h-full w-full object-cover"
          onError={() => setShowFallback(true)}
        />
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';
